import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Building2, User, Crown, Activity, HardDrive, FileText, History, Gift, XCircle, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';

export default function PlatformSubscriberDetail() {
  const { clinicId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [mau, setMau] = useState(null);
  const [storage, setStorage] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editPlan, setEditPlan] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [note, setNote] = useState('');
  // Iter 86 — Comp this clinic
  const [compDays, setCompDays] = useState(90);
  const [compPlan, setCompPlan] = useState('multi');
  const [compNote, setCompNote] = useState('');
  const [comping, setComping] = useState(false);
  const reloadDetail = async () => {
    const det = await api.get(`/platform/subscribers/${clinicId}`);
    setData(det.data);
    const au = await api.get(`/platform/subscribers/${clinicId}/audit?days=60`);
    setAudit(au.data);
  };
  const compClinic = async () => {
    setComping(true);
    try {
      const { data } = await api.post(`/platform/subscribers/${clinicId}/comp`, {
        duration_days: Number(compDays), plan: compPlan,
        note: compNote || undefined,
      });
      toast.success(`Comp granted until ${data.comp_until}.`);
      setCompNote('');
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setComping(false); }
  };
  const revokeComp = async () => {
    if (!await safeConfirm('Revoke this clinic\'s comp and flip status to canceled? They will need to subscribe via Stripe to keep access.')) return;
    setComping(true);
    try {
      await api.delete(`/platform/subscribers/${clinicId}/comp`);
      toast.success('Comp revoked.');
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setComping(false); }
  };
  const [upsellSending, setUpsellSending] = useState(false);
  const sendUpsell = async () => {
    if (!await safeConfirm('Send a friendly upgrade-recommendation email to this clinic\'s owner?')) return;
    setUpsellSending(true);
    try {
      const { data } = await api.post(`/platform/subscribers/${clinicId}/upsell-email`, {});
      toast.success(`Upsell email sent to ${data.to_email} (${data.from_plan} → ${data.to_plan}).`);
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setUpsellSending(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [det, m, st, au] = await Promise.all([
          api.get(`/platform/subscribers/${clinicId}`),
          api.get(`/usage/mau/${clinicId}?days=30`).catch(() => ({ data: null })),
          api.get(`/usage/storage/${clinicId}`).catch(() => ({ data: null })),
          api.get(`/platform/subscribers/${clinicId}/audit?days=60`).catch(() => ({ data: null })),
        ]);
        setData(det.data); setMau(m.data); setStorage(st.data); setAudit(au.data);
        setEditPlan(det.data.clinic?.plan || 'solo');
        setEditStatus(det.data.clinic?.subscription_status || 'trialing');
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [clinicId]);

  const overridePlan = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/subscribers/${clinicId}/plan`, {
        plan: editPlan, subscription_status: editStatus, note,
      });
      toast.success('Plan updated.');
      setNote('');
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  if (!data) return null;
  const c = data.clinic;
  const o = data.owner;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="subscriber-detail">
      <button onClick={() => nav('/platform/subscribers')}
              className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
              data-testid="back-to-subscribers">
        <ArrowLeft className="w-4 h-4"/> Back to subscribers
      </button>

      {/* Headline */}
      <header className="bento-card flex items-start gap-4 flex-wrap">
        <Building2 className="w-8 h-8 text-brand"/>
        <div className="flex-1 min-w-[280px]">
          <h1 className="font-heading text-2xl font-bold">{c.name}</h1>
          <p className="text-sm text-fg-muted">
            {c.slug && <span className="mr-2"><code className="text-xs">{c.slug}</code></span>}
            {c.clinic_id}
          </p>
          <div className="flex gap-3 flex-wrap mt-3 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-bg-secondary"><Crown className="w-3 h-3 inline mr-1"/>{c.plan}</span>
            <span className="px-2 py-0.5 rounded-full bg-bg-secondary">{c.subscription_status || 'trialing'}</span>
            <span className="px-2 py-0.5 rounded-full bg-bg-secondary">{c.country_code || '—'}</span>
            <span className="px-2 py-0.5 rounded-full bg-bg-secondary">Created {c.created_at?.slice(0,10) || '—'}</span>
            {c.trial_ends_at && <span className="px-2 py-0.5 rounded-full bg-amber-50">Trial → {c.trial_ends_at.slice(0,10)} ({data.trial_remaining_days}d)</span>}
            {c.comp_until && (
              <span className={`px-2 py-0.5 rounded-full font-semibold ${
                (data.comp_remaining_days ?? -1) >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
              }`} data-testid="comp-badge">
                <Gift className="w-3 h-3 inline mr-1"/>
                {(data.comp_remaining_days ?? -1) >= 0
                  ? `COMP'D · ${c.comp_until} (${data.comp_remaining_days}d)`
                  : `COMP EXPIRED · ${c.comp_until}`}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Counters */}
      <div className="grid md:grid-cols-5 gap-3" data-testid="subscriber-counters">
        {Object.entries(data.counters).map(([k, v]) => (
          <div key={k} className="bento-card text-center">
            <div className="text-2xl font-bold tabular-nums">{v}</div>
            <div className="text-[11px] text-fg-muted uppercase tracking-wider mt-1">{k.replace(/_/g, ' ')}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Owner card */}
        <div className="bento-card" data-testid="subscriber-owner">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-brand"/>
            <h2 className="font-heading font-semibold">Owner</h2>
          </div>
          {o ? (
            <div className="space-y-1.5 text-sm">
              <div><strong>{o.full_name || o.name}</strong></div>
              <div className="text-fg-muted">{o.email}</div>
              <div className="text-[11px] text-fg-muted">
                role={o.role} · clinic_id={o.clinic_id} · created {o.created_at?.slice(0,10)}
              </div>
              <button onClick={sendUpsell} disabled={upsellSending || c.plan === 'multi'}
                      className="mt-3 w-full inline-flex justify-center items-center gap-2 px-3 py-2 border border-soft rounded-md text-sm font-semibold hover:bg-bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                      title={c.plan === 'multi' ? 'Already on the top tier — nothing to upsell.' : 'Email this owner an upgrade recommendation'}
                      data-testid="upsell-email-btn">
                {upsellSending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4 text-brand"/>}
                {c.plan === 'multi' ? 'Already on top tier' : 'Send upgrade recommendation email'}
              </button>
            </div>
          ) : <p className="text-sm text-fg-muted">No owner assigned. The earliest admin will be auto-promoted on next access.</p>}
        </div>

        {/* MAU sparkline */}
        <div className="bento-card" data-testid="subscriber-mau">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-brand"/>
            <h2 className="font-heading font-semibold">Active Users (30d)</h2>
          </div>
          <div className="text-3xl font-bold">{mau?.unique_active_users ?? 0}</div>
          <div className="text-xs text-fg-muted mt-1">unique users logged in over the last 30 days</div>
          {mau?.daily?.length > 0 && (
            <div className="flex items-end gap-0.5 h-12 mt-3" data-testid="mau-sparkline">
              {mau.daily.map((d) => {
                const max = Math.max(...mau.daily.map(x => x.count), 1);
                const h = `${Math.round((d.count / max) * 100)}%`;
                return <div key={d.day} title={`${d.day}: ${d.count}`}
                            className="flex-1 bg-brand/60 hover:bg-brand transition-colors min-h-[2px]"
                            style={{ height: h }}/>;
              })}
            </div>
          )}
        </div>

        {/* Storage */}
        <div className="bento-card" data-testid="subscriber-storage">
          <div className="flex items-center gap-2 mb-3">
            <HardDrive className="w-4 h-4 text-brand"/>
            <h2 className="font-heading font-semibold">Storage</h2>
          </div>
          <div className="text-3xl font-bold">{storage?.total_mb ?? 0} MB</div>
          <div className="text-xs text-fg-muted mt-2 space-y-0.5">
            <div>Documents: {Math.round((storage?.documents_bytes || 0) / 1024 / 1024 * 100) / 100} MB</div>
            <div>Posture snapshots: {Math.round((storage?.posture_snapshots_bytes || 0) / 1024 / 1024 * 100) / 100} MB</div>
          </div>
        </div>

        {/* Iter 86 — One-click clinic comp */}
        <div className="bento-card" data-testid="comp-clinic-card">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-4 h-4 text-emerald-600"/>
            <h2 className="font-heading font-semibold">Comp this clinic</h2>
          </div>
          <p className="text-xs text-fg-muted mb-3">
            Grant a free subscription for trial extensions, support tickets, or friends-and-family deals.
            Sets <code>plan=multi</code> + <code>status=active</code> and records <code>comp_until</code>.
          </p>
          <div className="space-y-2 text-sm">
            <label className="block">
              <span className="text-xs text-fg-muted">Duration</span>
              <select value={compDays} onChange={(e) => setCompDays(e.target.value)}
                      className="block w-full border border-soft rounded px-2 py-1.5 mt-1"
                      data-testid="comp-duration-select">
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days (default)</option>
                <option value={180}>180 days</option>
                <option value={365}>365 days (1 year)</option>
                <option value={730}>730 days (2 years)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-fg-muted">Plan during comp</span>
              <select value={compPlan} onChange={(e) => setCompPlan(e.target.value)}
                      className="block w-full border border-soft rounded px-2 py-1.5 mt-1"
                      data-testid="comp-plan-select">
                <option value="solo">Solo</option>
                <option value="medium">Medium</option>
                <option value="multi">Multi (default)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-fg-muted">Note (recommended)</span>
              <input value={compNote} onChange={(e) => setCompNote(e.target.value)}
                     placeholder="e.g. friends-and-family · support ticket #1234"
                     className="block w-full border border-soft rounded px-2 py-1.5 mt-1"
                     data-testid="comp-note-input"/>
            </label>
            <div className="flex gap-2 mt-2">
              <button onClick={compClinic} disabled={comping}
                      className="btn-primary flex-1 inline-flex justify-center items-center gap-2"
                      data-testid="comp-grant-btn">
                {comping ? <Loader2 className="w-4 h-4 animate-spin"/> : <Gift className="w-4 h-4"/>}
                {c.comp_until ? 'Extend comp' : 'Grant comp'}
              </button>
              {c.comp_until && (
                <button onClick={revokeComp} disabled={comping}
                        className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-40"
                        data-testid="comp-revoke-btn">
                  <XCircle className="w-4 h-4 inline mr-1"/> Revoke
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Plan override (admin tool) */}
        <div className="bento-card" data-testid="subscriber-override">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-4 h-4 text-brand"/>
            <h2 className="font-heading font-semibold">Manual override</h2>
          </div>
          <p className="text-xs text-fg-muted mb-3">Use sparingly — Stripe events are the source of truth for live subscribers.</p>
          <div className="space-y-2 text-sm">
            <label className="block">
              <span className="text-xs text-fg-muted">Plan</span>
              <select value={editPlan} onChange={(e) => setEditPlan(e.target.value)}
                      className="block w-full border border-soft rounded px-2 py-1.5 mt-1"
                      data-testid="override-plan-select">
                <option value="solo">Solo</option>
                <option value="medium">Medium</option>
                <option value="multi">Multi</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-fg-muted">Subscription status</span>
              <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}
                      className="block w-full border border-soft rounded px-2 py-1.5 mt-1"
                      data-testid="override-status-select">
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="past_due">Past due</option>
                <option value="canceled">Canceled</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-fg-muted">Note (for audit log)</span>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="e.g. comp account, support request #1234"
                     className="block w-full border border-soft rounded px-2 py-1.5 mt-1"
                     data-testid="override-note-input"/>
            </label>
            <button onClick={overridePlan} disabled={saving}
                    className="btn-primary w-full inline-flex justify-center items-center gap-2 mt-2"
                    data-testid="override-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <FileText className="w-4 h-4"/>}
              Apply override
            </button>
          </div>
        </div>
      </div>

      {/* Audit log */}
      <div className="bento-card" data-testid="subscriber-audit">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-brand"/>
          <h2 className="font-heading font-semibold">Audit ({audit.length})</h2>
        </div>
        {audit.length === 0 ? <p className="text-sm text-fg-muted">No audit entries in the last 60 days.</p> : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
            {audit.map((a, i) => (
              <div key={i} className="text-xs flex gap-3 border-b border-soft/50 pb-1.5">
                <span className="text-fg-muted shrink-0">{a.created_at?.replace('T', ' ').slice(0,19)}</span>
                <span className="font-mono shrink-0">{a.action}</span>
                <span className="text-fg-muted truncate">
                  {a.from_plan && a.to_plan && `${a.from_plan} → ${a.to_plan}`}
                  {a.note && ` · ${a.note}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
